import React from 'react'
import {
	useApi,
	type APIRequest,
} from '@hooks/simulatore/use-api-requests.hook_new'
import { useAtom } from 'jotai'
import { dataStateAtom } from '@store/atoms'
import type { StartingValues, Token } from '@store/defaultReducerState'
import type { StepOptions } from './stepper/steps.config'

import type { TrasparenzaResponse } from '@store/defaultReducerState'

import { trasparenzaAtom, trasparenzaCodeAtom } from '@store/store'

import {
	activedStep,
	AMOUNT,
	CATEGORY,
	formDataAtom,
	INSURANCE_YES,
	INSURANCE,
	promotionalCodeAtom,
	selectedValueAtom,
	type DataSimulatore,
	INSURANCE_NO,
	SUB_CATEGORY,
	selectedSubStepAtom,
	SUB_CATEGORY_LABEL,
	RICHIESTA_TO_SIM_KEY,
} from '@store/store'
import { StepsSimulatore } from '@store/stepsSimulatore'

function _useAsyncDataLoadFromToken() {
	const [, setActiveStep] = useAtom(activedStep)

	const [state, setState] = useAtom(dataStateAtom)
	const [formData, setFormData] = useAtom(formDataAtom)

	const [selectedValueMap, setSelectedValueMap] = useAtom(selectedValueAtom)
	const [, setPromotionalCode] = useAtom(promotionalCodeAtom)
	const [, setSelectedSubStep] = useAtom(selectedSubStepAtom)
	// Defensive: state.defaults can be undefined if the atom was initialised
	// by a Jotai store reset or a partial setDataState call that omitted the key.
	const { sbToken } = state.defaults ?? {}

	// Capture at module-init time (before any effects) whether we navigated here
	// from the richiesta landing page. The Simulatore.tsx restore effect will
	// remove the key, so we cannot check it inside the sbToken effect below.
	const cameFromRichiesta = React.useRef(
		!!sessionStorage.getItem(RICHIESTA_TO_SIM_KEY)
	)

	const amountComparatore = state.data.One.Amount

	const [, setTrasparenza] = useAtom(trasparenzaAtom)
	const [savedTrasparenzaCode, setSavedTrasparenzaCode] =
		useAtom(trasparenzaCodeAtom)

	const [trasparenzaData, fetchTrasparenza, trasparenzaLoading] =
		useApi<TrasparenzaResponse>({
			request: 'DbOperations/getTrasparenza',
			method: 'GET',
			sleeper: true,
		})
	
	const isComparatore = state.token?.Provenienza === 'COMPARATORE'

	const initialFinancialTableRef = React.useRef(
		state.token?.StartingValues?.FinancialTableEntry ??
			state.token?.StartingValues?.FinancialTable ??
			'OQ1'
	)

	const financialTable = React.useMemo(() => {
		const sv = state.token?.StartingValues

		if (isComparatore) {
			return sv?.FinancialTable ?? 'OQ1'
		}

		if (sv?.CodiceAffinity) {
			return sv?.FinancialTable ?? 'OQ1'
		}

		return (
			formData.subCategory?.tabFin ??
			formData.category?.tabFin ??
			initialFinancialTableRef.current
		)
	}, [
		isComparatore,
		state.token?.StartingValues?.CodiceAffinity,
		state.token?.StartingValues?.FinancialTable,
		formData.category?.tabFin,
		formData.subCategory?.tabFin,
	])
	
	//chiama la trasparenza con la tabella finanziaria corretta e aggiorna lo startingValues
	React.useEffect(() => {
		if (!financialTable) return
		if (savedTrasparenzaCode === financialTable) return  //evita chiamate duplicate

		setSavedTrasparenzaCode(financialTable)

		setState((prev) => ({
			...prev,
			token: {
				...prev.token,
				StartingValues: {
					...prev.token.StartingValues,
					FinancialTable: financialTable,
				},
			},
		}))

		fetchTrasparenza({
			query: {
				codice: financialTable,
				moreInfo: '1',
			},
		})
	}, [financialTable, savedTrasparenzaCode])
	


	// API 1 → CARICA LA STRUTTURA DEGLI STEPS
	const [infoStruct, infoStructFetch, infostructLoading] = useApi<StepOptions>({
		request: 'DbOperations/getInfoStructSim',
		queryParams: { codiceStructure: 'AABBCC' }, //key api
		method: 'GET',
	})

	// API 2 → Rows Data
	const [rowsData, rowRefresh, fetchingRows] = useApi<Token>({
		request: 'DbOperations/CheckGetTokenSim',
		method: 'POST',
		sleeper: true,
	})

	React.useEffect(() => {
		// Skip API call when navigating back from richiesta — data is already
		// being restored from sessionStorage in Simulatore.tsx.
		if (sbToken && !cameFromRichiesta.current) {
			// evita doppie chiamate
			rowRefresh({
				body: {
					oToken: sbToken,
				},
			})
		}
	}, [sbToken])

	// Qui aggiorni l’atomo con il risultato dell’API QUESTI PARAMETRI SARANNO POI USATI da useUrlParams() ->tableDefaut
	React.useEffect(() => {
		if (rowsData) {
			setState((prev) => ({
				...prev,
				token: {
					...prev.token,
					...rowsData,
				},
			}))
			setActiveStep(rowsData.CurrentStep) //setto lo step dali dati del Token

			const amount = rowsData.Amount

			if (amount == null) {
				return
			}

			//fill field from token  if no exixt
			setFormData((prev): DataSimulatore => {
				if (prev.amount) return prev

				return {
					...prev,

					[AMOUNT]: {
						value: String(amount),
						label: `${Number(amount).toLocaleString()} €`,
					},
					[CATEGORY]: {
						value: rowsData.MotivationSelected,
					},
					[SUB_CATEGORY]: {
						value: rowsData.MotivationDetailSelected,
					},
					[SUB_CATEGORY_LABEL]: rowsData.DescrMotivazione,

					// Only restore insurance if the token has an explicit boolean value;
					// null/undefined means the user hadn't selected yet → leave as null
					...(rowsData.Insurance != null && {
						[INSURANCE]: {
							value: rowsData.Insurance === true ? INSURANCE_YES : INSURANCE_NO,
						},
					}),
					selectedInstallment:
						prev.selectedInstallment ?? rowsData.SelectedInstallment ?? null,

					selectedInstallmentSond:
						prev.selectedInstallmentSond ??
						rowsData.SelectedInstallmentSond ??
						null,

					codicePromozionale: rowsData.StartingValues?.CodiceAffinity,
				}
			})

			// Restore confirmed promo code atom so the UI banner and save payload reflect it
			if (rowsData.StartingValues?.CodiceAffinity) {
				setPromotionalCode(rowsData.StartingValues.CodiceAffinity)
			}

			// aggiorna l'opzione selezionata per i radio button
			setSelectedValueMap((prev) => ({
				...prev,
				[StepsSimulatore.IMPORTO]: rowsData.OtherValue ? '' : String(amount), //se other value azzero il campo del radio button
				[StepsSimulatore.PROGETTO]: rowsData.MotivationSelected,
				[StepsSimulatore.SUBPROGETTO]: rowsData.MotivationDetailSelected,
				[StepsSimulatore.ASSICURAZIONE]:
					rowsData.Insurance === true
						? INSURANCE_YES
						: rowsData.Insurance === false
							? INSURANCE_NO
							: '',
			}))
			//NECESSARIO PER ABILITARE IL BUTTON peosegui
			setSelectedSubStep(rowsData.MotivationDetailSelected)
		}
	}, [rowsData])

	React.useEffect(() => {
		if (!trasparenzaData) return

		setTrasparenza(trasparenzaData)
	}, [trasparenzaData, setTrasparenza])

	return {
		state,
		rowsData,
		rowRefresh,
		infoStruct,
		infostructLoading,
		fetchingRows,
		amountComparatore,
		trasparenzaData,
		trasparenzaLoading,
	}
}

function _useSaveDataForm(url: APIRequest) {
	const [state, setState] = useAtom(dataStateAtom)
	const [activeStep] = useAtom(activedStep)
	const [formData] = useAtom(formDataAtom)
	const [promotionalCode] = useAtom(promotionalCodeAtom)

	const [data, fetchData, loadingData] = useApi<Token>({
		request: url,
		method: 'POST',
		sleeper: true,
	})

	const saveDataForm = (email?: string) => {
		const payload: Token = {
			...state.token,
			CurrentStep: activeStep,
			ReqAmount: Number(formData.amount?.value),
			Amount: Number(formData.amount?.value),
			MotivationSelected: formData.category?.value ?? '',
			MotivationDetailSelected: formData.subCategory?.value ?? '',
			DescrMotivazione: formData.subCategorylabel ?? '',
			Installments: formData.installments?.Installments ?? [],
			SelectedInstallment: formData.selectedInstallment ?? null,
			SelectedInstallmentSond: formData.selectedInstallmentSond ?? null,
			// Send null when the user hasn't made a selection yet, so the backend
			// stores null and the token link restores an unselected state.
			Insurance:
				formData.insurance === null
					? null
					: formData.insurance.value === INSURANCE_YES,
			CodicePromozionale: promotionalCode,
			IsSecciVisible: false,
			eMail: email ?? formData.email ?? '',
			sessione: state.token.sessione ?? '',
			Disclaimer: formData.disclaimer ?? '',
		}

		fetchData({
			body: payload,
		})
	}

	return { data, fetchData: saveDataForm, loadingData }
}

export { _useAsyncDataLoadFromToken, _useSaveDataForm }
